import { AUTH_ROUTES } from "./authRoutes";
import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Carousel, Col, Form, Row } from "react-bootstrap";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import PhoneInput from "react-phone-input-2";
import { useMediaQuery } from "react-responsive";
import "react-phone-input-2/lib/style.css";
import { Shimmer } from "react-shimmer";
import "./SignIn.css";
import SigninBg from "../assets/images/signin-bg.svg";
import { signUpUser } from "../services/authService";
import { useTutorials } from "../hooks/useTutorials";

const INITIAL_FORM_STATE = {
  title: "MR.",
  name: "",
  email: "",
  password: "",
  confirmPassword: "",
  phone: "",
  referralCode: "",
};

const FALLBACK_TUTORIALS = [
  { id: "signup-fallback-1", title: "Your Trusted Partner for Government Exam Success", description: "Learn smarter, practice better, and move one step closer to your dream government job." },
  { id: "signup-fallback-2", title: "Your Trusted Partner for Government Exam Success", description: "Learn smarter, practice better, and move one step closer to your dream government job." },
  { id: "signup-fallback-3", title: "Your Trusted Partner for Government Exam Success", description: "Learn smarter, practice better, and move one step closer to your dream government job." },
];

function decodeTutorialText(value) {
  return String(value || "")
    .replace(/&amp;/g, "&")
    .replace(/&nbsp;/g, " ")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
}

function TutorialImage({ imageUrl }) {
  return (
    <div className="signin-tutorial-image-frame">
      <img
        src={imageUrl || SigninBg}
        alt=""
        className="signin-tutorial-image"
        onError={(event) => { event.currentTarget.src = SigninBg; }}
      />
    </div>
  );
}

function SignUpPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const attributedReferralCode = (searchParams.get("referral_code") || "").trim();
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const [formValues, setFormValues] = useState(() => ({ ...INITIAL_FORM_STATE, referralCode: attributedReferralCode }));
  const [countryData, setCountryData] = useState({ dialCode: "91", countryCode: "in" });
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTutorialIndex, setActiveTutorialIndex] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { data: tutorialData, isLoading: tutorialsLoading } = useTutorials("EN");
  const tutorials = tutorialData?.tutorials?.length ? tutorialData.tutorials : FALLBACK_TUTORIALS;

  useEffect(() => {
    if (attributedReferralCode) {
      setFormValues((values) => ({ ...values, referralCode: attributedReferralCode }));
    }
  }, [attributedReferralCode]);

  const phonePlaceholder = useMemo(
    () => (isMobile ? "Phone Number" : "Enter Phone No"),
    [isMobile]
  );

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    setFormValues((currentValues) => ({
      ...currentValues,
      [name]: value,
    }));

    setFieldErrors((currentErrors) => ({
      ...currentErrors,
      [name]: "",
    }));

    setSubmitError("");
  };

  const handlePhoneChange = (value, country) => {
    const dialCode = country?.dialCode || countryData.dialCode;
    const normalizedPhone = value.startsWith(dialCode) ? value.slice(dialCode.length) : value;

    setCountryData({
      dialCode,
      countryCode: country?.countryCode || countryData.countryCode,
    });

    setFormValues((currentValues) => ({
      ...currentValues,
      phone: normalizedPhone,
    }));

    setFieldErrors((currentErrors) => ({
      ...currentErrors,
      phone: "",
    }));
  };

  const validateForm = () => {
    const nextErrors = {};
    const trimmedName = formValues.name.trim();
    const trimmedEmail = formValues.email.trim().toLowerCase();
    const trimmedReferralCode = formValues.referralCode.trim();
    const trimmedPhone = formValues.phone.trim();

    if (!trimmedName) {
      nextErrors.name = "Full name is required.";
    }

    if (!trimmedEmail) {
      nextErrors.email = "Email is required.";
    } else if (!/\S+@\S+\.\S+/.test(trimmedEmail)) {
      nextErrors.email = "Enter a valid email address.";
    }

    if (!formValues.password) {
      nextErrors.password = "Password is required.";
    } else if (formValues.password.length < 6) {
      nextErrors.password = "Password must be at least 6 characters.";
    } else if (!/^(?=.*[A-Za-z])(?=.*\d)(?=.*[@!$#%]).{6,}$/.test(formValues.password)) {
      nextErrors.password = "Password must include a letter, a number, and a special character (@!$#%).";
    }

    if (!formValues.confirmPassword) {
      nextErrors.confirmPassword = "Confirm password is required.";
    } else if (formValues.confirmPassword !== formValues.password) {
      nextErrors.confirmPassword = "Confirm password must match the password.";
    }

    if (trimmedPhone && !/^\d{10,}$/.test(trimmedPhone)) {
      nextErrors.phone = "Phone number must be at least 10 digits.";
    }

    if (trimmedReferralCode && trimmedReferralCode.length > 20) {
      nextErrors.referralCode = "Referral code must be 20 characters or fewer.";
    }

    setFieldErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError("");

    try {
      const requestPayload = {
        title: formValues.title,
        name: formValues.name.trim(),
        email: formValues.email.trim().toLowerCase(),
        password: formValues.password,
        confirm_password: formValues.confirmPassword,
        country_code: `+${countryData.dialCode}`,
      };

      const trimmedPhone = formValues.phone.trim();
      if (trimmedPhone) {
        requestPayload.phone = trimmedPhone;
      }

      const trimmedReferralCode = formValues.referralCode.trim();
      if (trimmedReferralCode) {
        requestPayload.referral_code = trimmedReferralCode;
      }

      const { payload, meta } = await signUpUser(requestPayload);
      const userId = payload?.user_id;

      if (!userId) {
        throw new Error(meta?.message || "Sign up failed. Please try again.");
      }

      navigate(AUTH_ROUTES.verification, {
        replace: true,
        state: {
          userId,
          email: requestPayload.email,
          source: "signup",
        },
      });
    } catch (error) {
      setSubmitError(error.message || "Unable to sign up right now.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container-fluid signin-container auth-background">
      <div className="row auth-page-row">
        <div className="col-md-6 d-flex flex-column justify-content-center align-items-center text-white left-section p-0">
          <Carousel
            controls={false}
            indicators={!tutorialsLoading && tutorials.length > 1}
            interval={4000}
            fade
            activeIndex={Math.min(activeTutorialIndex, tutorials.length - 1)}
            onSelect={(selectedIndex) => setActiveTutorialIndex(selectedIndex)}
            className="w-100 d-flex align-items-center justify-content-center"
          >
            {tutorialsLoading ? (
              <Carousel.Item>
                <div className="text-center px-4 signin-tutorial-shimmer" aria-label="Loading tutorials">
                  <Shimmer width={320} height={28} className="mx-auto mb-3" />
                  <Shimmer width={420} height={18} className="mx-auto mb-4" />
                  <Shimmer width={362} height={350} className="mx-auto" />
                </div>
              </Carousel.Item>
            ) : tutorials.map((tutorial) => (
              <Carousel.Item key={tutorial.id}>
                <div className="text-center px-4">
                  <h4 className="fw-bold">{decodeTutorialText(tutorial.title)}</h4>
                  <p className="mb-2">{decodeTutorialText(tutorial.description)}</p>
                  <TutorialImage imageUrl={tutorial.imageUrl} />
                </div>
              </Carousel.Item>
            ))}
          </Carousel>

          {/* <div className="text-center">
            <Link to={AUTH_ROUTES.signIn}>
              <Button className="signupBtn fw-semibold px-5 py-2">Sign In</Button>
            </Link>
          </div> */}
        </div>

        <div className="col-md-6 d-flex justify-content-center align-items-center right-section position-relative signUp-Box">
          <div className="signin-box auth-form-card p-4 p-md-5 rounded">
            <h3 className="fw-semibold text-center mb-2">Sign Up</h3>
            <p className="text-center fw-medium mb-3 form-paagraph auth-form-intro">
              Create your account to continue with Vidya Kasam.
            </p>

            {submitError ? (
              <Alert className="mb-3" variant="danger">
                {submitError}
              </Alert>
            ) : null}

            <Form className="position-relative" noValidate onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>Full Name</Form.Label>
                <Row>
                  <Col xs={3} md={3} xl={2} className="pe-0">
                    <Form.Select name="title" value={formValues.title} onChange={handleInputChange}>
                      <option>MR.</option>
                      <option>MRS.</option>
                      <option>MS.</option>
                    </Form.Select>
                  </Col>
                  <Col xs={9} md={9} xl={10}>
                    <div className="auth-form-field">
                      <span className="auth-field-icon" aria-hidden="true"><i className="bi bi-person" /></span>
                      <Form.Control
                        isInvalid={Boolean(fieldErrors.name)}
                        name="name"
                        placeholder="Full Name"
                        type="text"
                        value={formValues.name}
                        onChange={handleInputChange}
                      />
                    </div>
                    <Form.Control.Feedback type="invalid">{fieldErrors.name}</Form.Control.Feedback>
                  </Col>
                </Row>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <div className="auth-form-field">
                  <span className="auth-field-icon" aria-hidden="true"><i className="bi bi-envelope" /></span>
                  <Form.Control
                    isInvalid={Boolean(fieldErrors.email)}
                    name="email"
                    placeholder={isMobile ? "Email" : "Enter your Email"}
                    type="email"
                    value={formValues.email}
                    onChange={handleInputChange}
                  />
                </div>
                <Form.Control.Feedback type="invalid">{fieldErrors.email}</Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Password</Form.Label>
                <div className="auth-form-field auth-password-field">
                  <span className="auth-field-icon" aria-hidden="true"><i className="bi bi-lock" /></span>
                  <Form.Control
                    isInvalid={Boolean(fieldErrors.password)}
                    name="password"
                    placeholder={isMobile ? "Password" : "Enter your Password"}
                    type={showPassword ? "text" : "password"}
                    value={formValues.password}
                    onChange={handleInputChange}
                  />
                  <button className="auth-password-toggle" type="button" aria-label={showPassword ? "Hide password" : "Show password"} aria-pressed={showPassword} onClick={() => setShowPassword((current) => !current)}>
                    <i className={`bi ${showPassword ? "bi-eye" : "bi-eye-slash"}`} aria-hidden="true" />
                  </button>
                </div>
                <Form.Control.Feedback type="invalid">{fieldErrors.password}</Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Confirm Password</Form.Label>
                <div className="auth-form-field auth-password-field">
                  <span className="auth-field-icon" aria-hidden="true"><i className="bi bi-lock" /></span>
                  <Form.Control
                    isInvalid={Boolean(fieldErrors.confirmPassword)}
                    name="confirmPassword"
                    placeholder={isMobile ? "Confirm Password" : "Confirm your Password"}
                    type={showConfirmPassword ? "text" : "password"}
                    value={formValues.confirmPassword}
                    onChange={handleInputChange}
                  />
                  <button className="auth-password-toggle" type="button" aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"} aria-pressed={showConfirmPassword} onClick={() => setShowConfirmPassword((current) => !current)}>
                    <i className={`bi ${showConfirmPassword ? "bi-eye" : "bi-eye-slash"}`} aria-hidden="true" />
                  </button>
                </div>
                <Form.Control.Feedback type="invalid">{fieldErrors.confirmPassword}</Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Enter Phone</Form.Label>
                <Row>
                  <Col xs={3} md={3} xl={2} className="pe-0">
                    <div className="auth-phone-code">
                      <PhoneInput
                        country={countryData.countryCode}
                        disableCountryCode
                        inputStyle={{ display: "none" }}
                        buttonStyle={{ border: "none", background: "transparent" }}
                        dropdownStyle={{ maxHeight: "150px" }}
                        value=""
                        onChange={handlePhoneChange}
                      />
                    </div>
                  </Col>
                  <Col xs={9} md={9} xl={10}>
                    <div className="auth-form-field">
                      <span className="auth-field-icon" aria-hidden="true"><i className="bi bi-telephone" /></span>
                      <Form.Control
                        isInvalid={Boolean(fieldErrors.phone)}
                        name="phone"
                        placeholder={phonePlaceholder}
                        type="tel"
                        value={formValues.phone}
                        onChange={handleInputChange}
                      />
                    </div>
                    <Form.Control.Feedback type="invalid">{fieldErrors.phone}</Form.Control.Feedback>
                  </Col>
                </Row>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Referral Code</Form.Label>
                <div className="auth-form-field">
                  <span className="auth-field-icon" aria-hidden="true"><i className="bi bi-ticket-perforated" /></span>
                  <Form.Control
                    isInvalid={Boolean(fieldErrors.referralCode)}
                    name="referralCode"
                    placeholder={isMobile ? "Referral Code" : "Enter Referral Code (Optional)"}
                    type="text"
                    value={formValues.referralCode}
                    onChange={handleInputChange}
                    readOnly={Boolean(attributedReferralCode)}
                  />
                </div>
                <Form.Control.Feedback type="invalid">{fieldErrors.referralCode}</Form.Control.Feedback>
              </Form.Group>

              <Button className="signin-btn w-100 fw-semibold px-5 py-2 signupBtn" disabled={isSubmitting} type="submit">
                {isSubmitting ? "Creating Account..." : "Sign Up"}
              </Button>
            </Form>

            <p className="text-center mt-3 terms-text">
              By signing up, you agree with our{" "}
              <Link to="/terms-and-conditions" className="fw-normal">
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link to="/privacy-policy" className="fw-normal ">
                Privacy Policy
              </Link>
              .
            </p>

            <p className="text-center mt-3 d-none d-md-block">
              Already have account ?
              <Link to={AUTH_ROUTES.signIn} className="fw-semibold text-decoration-none ms-1">
                Sign In
              </Link>
            </p>

            <p className="mb-0 dont-text d-flex d-md-none">
              Already have account ?  
              <Link to={AUTH_ROUTES.signIn} className="fw-semibold text-decoration-none ms-1">
                Sign In
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SignUpPage;
